import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Badge,
  Form,
} from 'react-bootstrap';
import { adminAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import {
  PlayCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const emptyForm = {
  title: '',
  url: '',
  sortOrder: 0,
  isActive: true,
};

const TrainingManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [videos, setVideos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminAPI.getTrainingVideos();
      setVideos(data.videos || []);
    } catch (err) {
      setError(getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const openCreateModal = () => {
    setEditingVideo(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (video) => {
    setEditingVideo(video);
    setForm({
      title: video.title || '',
      url: video.url || '',
      sortOrder: video.sortOrder ?? 0,
      isActive: video.isActive !== false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      message.error('Заполните название и ссылку');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        url: form.url.trim(),
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
      };

      if (editingVideo) {
        await adminAPI.updateTrainingVideo(editingVideo.id, payload);
        message.success('Видео обновлено');
      } else {
        await adminAPI.createTrainingVideo(payload);
        message.success('Видео добавлено');
      }

      setShowModal(false);
      await loadVideos();
    } catch (err) {
      message.error(getTranslatedError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Удалить видео «${video.title}»?`)) return;

    try {
      setDeletingId(video.id);
      await adminAPI.deleteTrainingVideo(video.id);
      message.success('Видео удалено');
      await loadVideos();
    } catch (err) {
      message.error(getTranslatedError(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Обучающие видео</h5>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={loadVideos}>
            <ReloadOutlined /> Обновить
          </Button>
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusOutlined /> Добавить видео
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : videos.length === 0 ? (
        <Alert variant="info">
          Видео пока не добавлены. Нажмите «Добавить видео» и вставьте ссылку с Rutube или YouTube.
        </Alert>
      ) : (
        <Table responsive hover className="align-middle">
          <thead>
            <tr>
              <th>#</th>
              <th>Название</th>
              <th>Ссылка</th>
              <th>Статус</th>
              <th style={{ width: 120 }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => (
              <tr key={video.id}>
                <td>{video.sortOrder ?? 0}</td>
                <td>{video.title}</td>
                <td>
                  <a href={video.url} target="_blank" rel="noopener noreferrer">
                    {video.url.length > 50 ? `${video.url.slice(0, 50)}…` : video.url}
                  </a>
                </td>
                <td>
                  {video.isActive ? (
                    <Badge bg="success">Активно</Badge>
                  ) : (
                    <Badge bg="secondary">Скрыто</Badge>
                  )}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1"
                    onClick={() => openEditModal(video)}
                    title="Редактировать"
                  >
                    <EditOutlined />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(video)}
                    disabled={deletingId === video.id}
                    title="Удалить"
                  >
                    <DeleteOutlined />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <PlayCircleOutlined className="me-2" />
            {editingVideo ? 'Редактировать видео' : 'Добавить видео'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название</Form.Label>
              <Form.Control
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Как создать протокол"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ссылка на видео</Form.Label>
              <Form.Control
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://rutube.ru/video/private/..."
              />
              <Form.Text className="text-muted">
                Поддерживаются Rutube (включая приватные ссылки) и YouTube
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Порядок сортировки</Form.Label>
              <Form.Control
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                min={0}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="training-video-active"
              label="Показывать пользователям"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TrainingManagement;
